// Form controls: textarea, select, checkbox, radio, toggle, and their groups.
// All render real form elements, so they submit, validate and label like plain HTML.
import { I80Element, define, h, nextId } from "../base.js";
import { errorParts, field, forward, indicator } from "../field.js";

const FIELD_ATTRS = ["label", "helper-text", "error", "invalid", "required", "optional", "disabled", "readonly", "width", "height"];

function syncStates(host, control) {
  if (host.hasAttribute("required")) control.required = host.flag("required");
  if (host.hasAttribute("disabled")) control.disabled = host.flag("disabled");
  if ("readOnly" in control && host.hasAttribute("readonly")) control.readOnly = host.flag("readonly");
}

// --- Textarea --------------------------------------------------------------

/** <i80-textarea label="Notes" name="notes" rows="6"></i80-textarea> (or with your own <textarea> inside) */
export class I80Textarea extends I80Element {
  static get observedAttributes() {
    return FIELD_ATTRS;
  }

  setup() {
    let ta = this.part("control") ?? this.querySelector("textarea");
    if (!ta) {
      ta = h("textarea", { rows: "4" });
      forward(this, ta);
      if (this.hasAttribute("value")) ta.value = this.getAttribute("value");
    }
    ta.setAttribute("data-i80-part", "control");
    this._ta = ta;
    this._render = field(this, ta);
  }

  update() {
    syncStates(this, this._ta);
    const { invalid } = this._render();
    this._ta.className = ["i80-form-textarea", "i80-typography-body-200", "i80-font-weight-regular", invalid && "i80-form-textarea--is-invalid"].filter(Boolean).join(" ");
    this._ta.style.width = this.getAttribute("width") ?? "";
    this._ta.style.height = this.getAttribute("height") ?? "";
  }

  get value() {
    return this._ta?.value ?? "";
  }

  set value(v) {
    if (this._ta) this._ta.value = v;
  }
}

// --- Select ----------------------------------------------------------------

/**
 * <i80-select label="Status" name="status">
 *   <option value="draft">Draft</option>
 *   <option value="done" selected>Done</option>
 * </i80-select>
 * A <select> written inside (e.g. Django's {{ form.status }}) is used as is.
 */
export class I80Select extends I80Element {
  static get observedAttributes() {
    return FIELD_ATTRS;
  }

  setup() {
    let sel = this.part("control") ?? this.querySelector("select");
    if (!sel) {
      sel = h("select");
      forward(this, sel, []);
      sel.removeAttribute("value");
      sel.append(...[...this.children].filter((c) => c.matches("option, optgroup")));
      if (this.hasAttribute("value")) sel.value = this.getAttribute("value");
    }
    sel.setAttribute("data-i80-part", "control");
    this._sel = sel;
    this._render = field(this, sel);
  }

  adopt() {
    // Options can arrive after the element (template engines, streamed HTML).
    for (const node of this.takeContent()) {
      if (node.nodeType === 1 && node.matches("option, optgroup")) this._sel.append(node);
      else if (node.nodeType === 1 && node.localName === "select" && node !== this._sel) {
        node.remove();
        this._sel.append(...node.children);
      }
    }
    if (this.hasAttribute("value")) this._sel.value = this.getAttribute("value");
  }

  update() {
    syncStates(this, this._sel);
    const { invalid } = this._render();
    this._sel.className = ["i80-form-select", "i80-typography-body-200", "i80-font-weight-regular", invalid && "i80-form-select--is-invalid"].filter(Boolean).join(" ");
    this._sel.style.width = this.getAttribute("width") ?? "";
  }

  get value() {
    return this._sel?.value ?? "";
  }

  set value(v) {
    if (this._sel) this._sel.value = v;
  }
}

// --- Checkbox, radio, toggle -------------------------------------------------

class FlagControl extends I80Element {
  static get observedAttributes() {
    return [...FIELD_ATTRS, "checked"];
  }

  setup() {
    let input = this.part("control") ?? this.querySelector(`input[type="${this.inputType}"]`);
    if (!input) {
      input = h("input", { type: this.inputType });
      forward(this, input);
      input.checked = this.flag("checked");
    }
    input.setAttribute("data-i80-part", "control");
    const radioName = this.closest("i80-radio-group")?.getAttribute("name");
    if (this.inputType === "radio" && radioName && !input.name) input.name = radioName;
    this._input = input;
    // Once the user has changed it, the checked attribute no longer drives the control (like native inputs).
    input.addEventListener("change", () => (this._touched = true));
    this._render = field(this, input, { layout: "flag", controlWrapper: this.wrap(input) });
  }

  wrap(input) {
    return input;
  }

  update() {
    syncStates(this, this._input);
    if (this.hasAttribute("checked") && !this._touched) this._input.checked = this.flag("checked");
    this._render();
  }

  get checked() {
    return this._input?.checked ?? this.flag("checked");
  }

  set checked(v) {
    if (this._input) this._input.checked = !!v;
  }

  get value() {
    return this._input?.value ?? "";
  }
}

/** <i80-checkbox label="Send me updates" name="updates" checked></i80-checkbox> */
export class I80Checkbox extends FlagControl {
  get inputType() {
    return "checkbox";
  }

  update() {
    this._input.className = "i80-form-checkbox";
    super.update();
  }
}

/** <i80-radio label="Weekly" value="weekly"></i80-radio>, usually inside <i80-radio-group name="..."> */
export class I80Radio extends FlagControl {
  get inputType() {
    return "radio";
  }

  update() {
    this._input.className = "i80-form-radio";
    super.update();
  }
}

/** <i80-toggle label="Notifications" name="notify"></i80-toggle>: a checkbox with role="switch". */
export class I80Toggle extends FlagControl {
  get inputType() {
    return "checkbox";
  }

  wrap(input) {
    const box = input.closest(".i80-form-toggle") ?? h("div", { class: "i80-form-toggle" }, input, h("div", { class: "i80-form-toggle__facade" }));
    input.setAttribute("role", "switch");
    const sync = () => input.setAttribute("aria-checked", String(input.checked));
    input.addEventListener("change", sync);
    sync();
    return box;
  }

  update() {
    this._input.className = "i80-form-toggle__control";
    super.update();
    this._input.setAttribute("aria-checked", String(this._input.checked));
  }
}

// --- Groups (fieldset + legend) ----------------------------------------------

/**
 * <i80-radio-group legend="Frequency" name="frequency" layout="horizontal">
 *   <i80-radio label="Daily" value="daily"></i80-radio>
 *   <i80-radio label="Weekly" value="weekly" checked></i80-radio>
 * </i80-radio-group>
 */
export class I80FormGroupBase extends I80Element {
  static get observedAttributes() {
    return ["legend", "helper-text", "error", "layout", "required", "optional", "name"];
  }

  setup() {
    let fs = this.part("fieldset");
    if (!fs) {
      const controls = [...this.children];
      fs = h("fieldset", { "data-i80-part": "fieldset" }, h("div", { class: "i80-form-group__control-fields-wrapper", "data-i80-part": "controls" }, controls));
      this.append(fs);
    }
    this._fs = fs;
    fs.id = nextId(this.localName);
  }

  adopt() {
    this._fs.querySelector('[data-i80-part="controls"]').append(...this.takeContent());
  }

  update() {
    const fs = this._fs;
    const id = fs.id;
    fs.className = `i80-form-group i80-form-group--layout-${this.choice("layout", ["vertical", "horizontal"], "vertical")}`;
    for (const el of fs.querySelectorAll(":scope > legend, :scope > .i80-form-group__helper-text, :scope > .i80-form-group__error")) el.remove();
    const controls = fs.querySelector('[data-i80-part="controls"]');
    const legend = this.getAttribute("legend");
    if (legend) fs.prepend(h("legend", { class: "i80-form-legend i80-form-group__legend", id: `legend-${id}` }, legend, ...indicator(this)));
    const helper = this.getAttribute("helper-text");
    if (helper) controls.before(h("div", { class: "i80-form-helper-text i80-form-group__helper-text", id: `helper-text-${id}` }, helper));
    const error = this.getAttribute("error");
    if (error) {
      controls.after(h("div", { class: "i80-form-error i80-form-group__error", id: `error-${id}` }, ...errorParts([error])));
    }
    const describedBy = [helper && `helper-text-${id}`, error && `error-${id}`].filter(Boolean).join(" ");
    for (const input of controls.querySelectorAll("input")) {
      const own = (input.getAttribute("aria-describedby") ?? "").split(" ").filter((x) => x && !x.includes(`-${id}`));
      const all = [...own, ...describedBy.split(" ").filter(Boolean)];
      if (all.length) input.setAttribute("aria-describedby", all.join(" "));
      if (error) input.setAttribute("aria-invalid", "true");
    }
    const name = this.getAttribute("name");
    if (name) for (const r of controls.querySelectorAll('input[type="radio"]:not([name])')) r.name = name;
  }
}

export class I80CheckboxGroup extends I80FormGroupBase {}
export class I80RadioGroup extends I80FormGroupBase {}
export class I80ToggleGroup extends I80FormGroupBase {}

define("i80-textarea", I80Textarea);
define("i80-select", I80Select);
define("i80-checkbox", I80Checkbox);
define("i80-radio", I80Radio);
define("i80-toggle", I80Toggle);
define("i80-checkbox-group", I80CheckboxGroup);
define("i80-radio-group", I80RadioGroup);
define("i80-toggle-group", I80ToggleGroup);
